<template>
  <fieldset
    class="relative -mx-4 p-4 py-2 @container/setting first:-mt-2 last:-mb-2"
    :data-emphasis-identifier="id"
  >
    <div class="flex flex-col gap-x-4 gap-y-2 @2xl/setting:flex-row">
      <div class="min-w-0 grow basis-0">
        <div class="flex items-center gap-3">
          <div
            v-if="slots.icon"
            class="grid size-4 shrink-0 place-items-center"
          >
            <slot name="icon"></slot>
          </div>

          <label
            :for="settingId"
            class="min-w-0 break-words text-sm font-medium"
          >
            <slot name="header"></slot>
          </label>
        </div>

        <p
          v-if="slots.description"
          class="break-words text-sm font-medium text-muted-foreground"
        >
          <slot name="description"></slot>
        </p>
      </div>

      <div
        class="flex min-w-0 grow basis-0 flex-col items-start text-left @2xl:items-end @2xl:text-right"
      >
        <slot></slot>
      </div>
    </div>
  </fieldset>
</template>

<script setup lang="ts">
import * as Sentry from "@sentry/vue";
import { onMounted, provide, useId } from "vue";

import { SETTING_CONTROL_KEY } from "./context";

const slots = defineSlots<{
  icon(): unknown;
  header(): unknown;
  description(): unknown;
  default(): unknown;
}>();

const props = withDefaults(
  defineProps<{
    type?: "single" | "group";
    id?: string;
  }>(),
  {
    type: "single",
    direction: "responsive",
  }
);

// We don't want this to change.
const type = props.type;

const settingId = useId()!;
let registeredControls = 0;
let controlHasMultipleInputs = false;

provide(SETTING_CONTROL_KEY, {
  register({ multiple = false } = {}) {
    registeredControls++;
    controlHasMultipleInputs ||= multiple;

    if (type === "single") {
      return settingId;
    }
  },
});

// TODO: Later on, we should look into moving this as a dev-only error.
const err = (msg: string) => {
  console.error(msg);
  Sentry.captureMessage(msg, "error");
};

onMounted(() => {
  // We can only warn for `single` here because `group` could include
  // conditional inputs and that's something we can't catch.
  if (type !== "single") {
    return;
  }

  if (registeredControls === 0) {
    err(`<Setting>: no controls mounted`);
  }

  if (registeredControls > 1 || controlHasMultipleInputs) {
    err(
      `<Setting>: multiple controls are mounted despite 'type' being set to 'single'`
    );
  }
});
</script>
